import React from "react";
import {
  Box,
  Image,
  Text,
  Button,
  SimpleGrid,
  Flex,
  Heading,
  Container,
  Badge,
  HStack,
  VStack,
  Icon,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { FiCheck, FiArrowRight, FiCpu, FiBookOpen, FiDatabase, FiAward } from "react-icons/fi";
import TopOffers from "../Home/homeComponents/TopOffersRecommended";

export default function NavneetSoftwares() {
  const cards = [
    {
      title: "Supreme Today AI",
      tag: "AI Legal Research",
      description:
        "Ultimate AI-driven legal research tool & single largest collection of law information by Vikas Publications.",
      features: ["Supreme Court & All High Courts", "AI Semantic Search", "Privy Council & Tribunals"],
      imgSrc: "./supremeai.jpeg",
      link: "/supremeAi",
    },
    {
      title: "CDJ Law Journal",
      tag: "Online Law Reporter",
      description:
        "India's leading internet-based law reporter delivering instant access to reported and unreported judgments.",
      features: ["Daily judgment updates", "Headnotes & Subject Index", "Comprehensive Citation Cross-Search"],
      imgSrc: "./cdj-law-journal.jpeg",
      link: "/CDJLawJournal",
    },
    {
      title: "Digilegal",
      tag: "Bombay Cases Reporter",
      description:
        "Complete online law journal of the Bombay High Court and Maharashtra tribunals with authoritative citations.",
      features: ["Complete Bombay Cases Archive", "Civil & Criminal Acts", "Advanced Keyword Search"],
      imgSrc: "./digilegal.jpeg",
      link: "/digileagal",
    },
    {
      title: "Legit Quest",
      tag: "i-DRAF AI Search",
      description:
        "Next-generation legal tech intelligence platform run by tech-savvy attorneys for hyper-fast case law retrieval.",
      features: ["Decision categorization", "Reverse citation mapping", "Bench insights & analytics"],
      imgSrc: "./legit-quest.jpeg",
      link: "/LegitQuest",
    },
    {
      title: "DLT Publications Online",
      tag: "Delhi Law Times",
      description:
        "Authoritative web-based portal and trusted digital resource for Delhi High Court and Supreme Court case judgments.",
      features: ["Authentic reporting", "Prompt weekly releases", "Reliable volume citations"],
      imgSrc: "./dlt-publications.jpeg",
      link: "/DltPublications",
    },
    {
      title: "Taxman",
      tag: "Taxation & Corporate Law",
      description:
        "The standard of excellence and skill in Direct Taxes, GST, Company Law, and International Corporate Taxation.",
      features: ["Income Tax & GST Judgments", "Case commentaries & circulars", "Rule-amendment updates"],
      imgSrc: "./taxman.jpeg",
      link: "/taxman",
    },
  ];

  return (
    <Box bg="#F8FAFC" minH="100vh">
      {/* Hero Banner Section */}
      <Box
        bgGradient="linear(to-b, #0B132B, #162237)"
        color="white"
        py={{ base: 12, md: 16 }}
        px={{ base: 4, md: 8 }}
        textAlign="center"
        position="relative"
        overflow="hidden"
      >
        <Container maxW="900px">
          <Badge
            bg="whiteAlpha.200"
            color="#EAB308"
            px={3}
            py={1}
            rounded="full"
            fontSize="xs"
            fontWeight="bold"
            letterSpacing="wider"
            mb={4}
          >
            ⚖️ AUTHORIZED LEGAL TECH & RESEARCH DISTRIBUTOR
          </Badge>

          <Heading
            as="h1"
            fontSize={{ base: "28px", md: "42px" }}
            fontWeight="extrabold"
            letterSpacing="tight"
            lineHeight="shorter"
            mb={4}
          >
            Digital Legal Intelligence & Research Softwares
          </Heading>

          <Text
            fontSize={{ base: "sm", md: "md" }}
            color="gray.300"
            maxW="700px"
            mx="auto"
            lineHeight="tall"
          >
            Empower your chamber, law firm, or corporate legal department with India's premier AI-enabled legal research suites, law reporters, and judgment archives.
          </Text>

          {/* Key Value Propositions */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mt={8} pt={4} borderTop="1px solid" borderColor="whiteAlpha.200">
            <HStack justify="center" spacing={2}>
              <Icon as={FiCpu} color="#EAB308" />
              <Text fontSize="xs" fontWeight="semibold">AI Semantic Search</Text>
            </HStack>
            <HStack justify="center" spacing={2}>
              <Icon as={FiDatabase} color="#EAB308" />
              <Text fontSize="xs" fontWeight="semibold">All India Jurisdictions</Text>
            </HStack>
            <HStack justify="center" spacing={2}>
              <Icon as={FiBookOpen} color="#EAB308" />
              <Text fontSize="xs" fontWeight="semibold">Authentic Citations</Text>
            </HStack>
            <HStack justify="center" spacing={2}>
              <Icon as={FiAward} color="#EAB308" />
              <Text fontSize="xs" fontWeight="semibold">Official Licenses</Text>
            </HStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Software Catalog Grid */}
      <Container maxW="1200px" py={{ base: 8, md: 12 }} px={{ base: 3, md: 6 }}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 6, lg: 8 }}>
          {cards.map((card, index) => (
            <Box
              key={index}
              bg="white"
              rounded="2xl"
              border="1px solid"
              borderColor="gray.200"
              shadow="xs"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              transition="all 0.25s ease"
              _hover={{
                transform: "translateY(-4px)",
                shadow: "lg",
                borderColor: "#162237",
              }}
            >
              {/* Card Image Header */}
              <Box
                h="180px"
                bg="#F1F5F9"
                p={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
              >
                <Image
                  src={card.imgSrc}
                  alt={card.title}
                  maxH="150px"
                  maxW="85%"
                  objectFit="contain"
                  fallbackSrc="https://navneetlawstudio.com/logo.png"
                  transition="transform 0.3s"
                  _hover={{ transform: "scale(1.04)" }}
                />
                <Badge
                  position="absolute"
                  top={3}
                  right={3}
                  bg="#162237"
                  color="#EAB308"
                  fontSize="10px"
                  px={2}
                  py={0.5}
                  rounded="md"
                  fontWeight="bold"
                >
                  {card.tag}
                </Badge>
              </Box>

              {/* Card Content Body */}
              <Box p={5} display="flex" flexDirection="column" flex="1">
                <Heading as="h3" size="md" color="#162237" fontWeight="bold" mb={2}>
                  {card.title}
                </Heading>

                <Text fontSize="xs" color="gray.600" lineHeight="tall" mb={4} flex="1">
                  {card.description}
                </Text>

                {/* Features List */}
                <VStack spacing={1.5} align="stretch" mb={5} pt={2} borderTop="1px solid" borderColor="gray.100">
                  {card.features.map((feat, fIdx) => (
                    <HStack key={fIdx} spacing={2} fontSize="xs" color="gray.700">
                      <Icon as={FiCheck} color="green.600" boxSize={3.5} flexShrink={0} />
                      <Text noOfLines={1}>{feat}</Text>
                    </HStack>
                  ))}
                </VStack>

                {/* CTA Button */}
                <Link to={card.link} style={{ width: "100%", marginTop: "auto" }}>
                  <Button
                    w="100%"
                    size="sm"
                    bg="#162237"
                    color="white"
                    _hover={{ bg: "#0F172A", shadow: "sm" }}
                    _active={{ bg: "#0B132B" }}
                    rounded="lg"
                    fontWeight="semibold"
                    rightIcon={<FiArrowRight />}
                  >
                    View Plans & Access
                  </Button>
                </Link>
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Recommended Books Section */}
      <TopOffers />
    </Box>
  );
}

